import React from 'react';
import {Platform, StyleSheet, Text, View, type StyleProp, type ViewStyle} from 'react-native';

import {fonts} from '@/shared/ui/fonts';
import {ICON_GLYPHS, type IconGlyphName} from '@/shared/ui/icon-glyphs';

/**
 * Значки кабинета — Lucide, набор shadcn/ui.
 *
 * ## Что это заменило
 *
 * Тысячу строк значков, нарисованных из `View`: полосок, колец и рамок, повёрнутых и
 * сложенных в календарь, терминал или человека. Они держались на одном пикселе — на
 * 16pt «терминал» и «монитор» становились одинаковыми коробочками, а «люди» — двумя
 * кляксами. Настоящий набор рисует профессионал, и рисует его в 24-точечной сетке с
 * штрихом 2, который одинаково читается на 14 и на 40.
 *
 * ## Почему шрифт, а не SVG
 *
 * `lucide-react-native` тянет `react-native-svg` — нативный модуль и пересборку
 * Android ради значков. Шрифтовой глиф — это `<Text>`: он есть на обеих платформах
 * бесплатно и доезжает до устройства тем же путём, что IBM Plex (`assets/fonts`,
 * `@font-face` в `web/index.html`). Файл вырезан до глифов, которые кабинет называет
 * по имени, — 19 КБ вместо 890; список и генератор — `tools/lucide-font.cjs`.
 *
 * ## Имена
 *
 * Имя значка — это его СМЫСЛ в кабинете («devices», «bulk», «logout»), а не рисунок.
 * Какой рисунок Lucide за ним стоит, решается в одном месте, в генераторе, и меняется
 * одной строкой. Так экран говорит «покажи выход», а не «покажи стрелку из двери».
 */
export type IconName = IconGlyphName;

export type IconProps = {
  name: IconName;
  color: string;
  /** Размер коробки глифа. По умолчанию — как на панели вкладок. */
  size?: number;
  style?: StyleProp<ViewStyle>;
};

const styles = StyleSheet.create({
  box: {alignItems: 'center', justifyContent: 'center'},
  glyph: {
    fontFamily: fonts.icon,
    textAlign: 'center',
    // Глиф занимает ровно свой кегль: без этого Android дорисовывает поля шрифта
    // сверху и снизу, и значок в 16pt строке уезжает вниз.
    includeFontPadding: false,
    ...(Platform.OS === 'web' ? {userSelect: 'none' as const} : null),
  },
});

export function Icon({name, color, size = 22, style}: IconProps) {
  return (
    <View
      style={[styles.box, {width: size, height: size}, style]}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants">
      <Text
        allowFontScaling={false}
        selectable={false}
        numberOfLines={1}
        style={[styles.glyph, {fontSize: size, lineHeight: size, color}]}>
        {ICON_GLYPHS[name]}
      </Text>
    </View>
  );
}
